import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Search, FileText, Database, Clock, Loader2 } from "lucide-react";
import NebulaBackground from "./components/NebulaBackground";
import StatCard from "./components/StatCard";
import Filters, { FilterState } from "./components/Filters";
import ResultCard from "./components/ResultCard";
import DetailPanel from "./components/DetailPanel";
import {
  searchDocs,
  fetchStats,
  type SearchHit,
  type StatsResponse,
} from "./lib/api";

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const id = window.setTimeout(() => setD(v), ms);
    return () => window.clearTimeout(id);
  }, [v, ms]);
  return d;
}

function lastIndexLabel(t: number | string | undefined): string {
  if (!t) return "—";
  const ms = typeof t === "string" ? Date.parse(t) : t < 1e12 ? t * 1000 : t;
  if (!isFinite(ms)) return "—";
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function App() {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 300);
  const [filters, setFilters] = useState<FilterState>({
    types: [],
    machines: [],
    dateDays: 0,
  });
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [total, setTotal] = useState(0);
  const [tookMs, setTookMs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [selected, setSelected] = useState<SearchHit | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Stats on mount
  useEffect(() => {
    const ac = new AbortController();
    fetchStats(ac.signal)
      .then(setStats)
      .catch(() => {
        /* silent */
      });
    return () => ac.abort();
  }, []);

  // Search
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setHits([]);
      setTotal(0);
      setError(null);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    const since =
      filters.dateDays > 0
        ? Math.floor(Date.now() / 1000) - filters.dateDays * 86400
        : undefined;
    searchDocs(
      {
        q: debouncedQuery,
        types: filters.types,
        machines: filters.machines,
        since,
      },
      ac.signal
    )
      .then((r) => {
        setHits(r.hits || []);
        setTotal(r.total ?? r.hits?.length ?? 0);
        setTookMs(r.took_ms ?? 0);
      })
      .catch((e: unknown) => {
        if ((e as { name?: string })?.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Search failed");
        setHits([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [debouncedQuery, filters]);

  // Cmd+K focus
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const resultsKey = useMemo(
    () => `${debouncedQuery}-${hits.length}`,
    [debouncedQuery, hits.length]
  );

  return (
    <div className="min-h-screen page-enter">
      <NebulaBackground />

      <main className="max-w-5xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-24">
        {/* Hero */}
        <header className="text-center">
          <div className="eyebrow">docs.willbracken.com</div>
          <h1 className="display-1 mt-3">Document Search</h1>
          <p className="mt-3 text-slate-400 text-base md:text-lg">
            Across all your machines and cloud.
          </p>
        </header>

        {/* Stats strip */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Files indexed"
            value={stats?.total_files ?? 0}
            icon={FileText}
            accent="text-accent-blue"
          />
          <StatCard
            label="Dedupe savings"
            value={stats?.dedupe_savings_bytes ?? 0}
            icon={Database}
            accent="text-accent-violet"
            format="bytes"
          />
          <StatCard
            label="Last index"
            value={lastIndexLabel(stats?.last_index_time)}
            icon={Clock}
            accent="text-accent-cyan"
          />
        </section>

        {/* Search input */}
        <section className="mt-8">
          <div className="relative glass-prominent ring-glass">
            <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
              {loading ? (
                <Loader2 size={20} className="animate-spin text-accent-blue" />
              ) : (
                <Search size={20} strokeWidth={2} />
              )}
            </div>
            <input
              ref={inputRef}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask anything. Try: 'Q3 budget', 'cat photos', 'meeting notes April'"
              className="w-full bg-transparent pl-14 pr-24 py-5 md:py-6 text-base md:text-lg text-slate-100 placeholder:text-slate-500 outline-none rounded-2xl"
              aria-label="Search documents"
            />
            <kbd className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 items-center gap-1 px-2 py-1 rounded-md border border-white/[0.08] bg-white/[0.03] text-[10px] font-mono text-slate-500">
              <span>{navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}</span>
              <span>K</span>
            </kbd>
          </div>
          <div className="mt-3">
            <Filters state={filters} onChange={setFilters} />
          </div>
        </section>

        {/* Result meta */}
        {debouncedQuery && (
          <div className="mt-6 flex items-center justify-between text-xs font-mono text-slate-500">
            <div>
              {loading
                ? "Searching…"
                : error
                ? <span className="text-accent-rose">Error: {error}</span>
                : `${total.toLocaleString()} results`}
            </div>
            {!loading && tookMs > 0 && <div>{tookMs}ms</div>}
          </div>
        )}

        {/* Results list */}
        <motion.section
          key={resultsKey}
          initial={{ opacity: 0.6, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="mt-4 space-y-3"
        >
          {!debouncedQuery && !loading && (
            <div className="glass-subtle ring-glass p-10 text-center text-slate-500 text-sm">
              Start typing to search across documents, code, images and spreadsheets.
            </div>
          )}
          {debouncedQuery && !loading && !error && hits.length === 0 && (
            <div className="glass-subtle ring-glass p-10 text-center text-slate-500 text-sm">
              No matches for{" "}
              <span className="text-slate-300 font-medium">"{debouncedQuery}"</span>.
            </div>
          )}
          {hits.map((h) => (
            <ResultCard
              key={h.id}
              hit={h}
              query={debouncedQuery}
              onSelect={() => setSelected(h)}
            />
          ))}
        </motion.section>

        <footer className="mt-16 text-center text-[11px] font-mono text-slate-600">
          docs.willbracken.com · gated by Cloudflare Access
        </footer>
      </main>

      <DetailPanel hit={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
